import { useCallback, useRef, useEffect } from 'react';

const POPUP_NAME = 'SingularOAuthPopup';
const POPUP_FEATURES = 'status=1,height=500,width=1024';
const WAITING_PAGE = '/static/dashboard/html/datasources/source_auth_waiting.html';

/**
 * Hook for managing OAuth popup lifecycle.
 * Handles popup opening, callback registration, and cleanup.
 *
 * @returns {{openPopup: Function, redirectPopup: Function, closePopup: Function, registerCallback: Function}} OAuth popup controls
 */
export default function useOAuthPopup() {
    const popupRef = useRef(null);
    const callbackRef = useRef(null);

    const closePopup = useCallback(() => {
        if (popupRef.current && !popupRef.current.closed) {
            popupRef.current.close();
        }
        popupRef.current = null;
    }, []);

    // Register the global callback handler that OAuth redirect page calls
    useEffect(() => {
        window.handleAuthPopupModal = (accessToken, errorMessage) => {
            if (callbackRef.current) {
                callbackRef.current(accessToken, errorMessage);
            }
            closePopup();
        };

        return () => {
            delete window.handleAuthPopupModal;
            closePopup();
        };
    }, [closePopup]);

    const openPopup = useCallback((title = 'Authorize Singular') => {
        const popup = window.open(WAITING_PAGE, `${title} :: ${POPUP_NAME}`, POPUP_FEATURES);

        if (!popup || popup.closed) {
            throw new Error('Popup was blocked. Please allow popups for this site.');
        }

        popupRef.current = popup;
        popup.focus();

        return popup;
    }, []);

    const redirectPopup = useCallback(url => {
        if (popupRef.current && !popupRef.current.closed) {
            popupRef.current.location.href = url;
        }
    }, []);

    const registerCallback = useCallback(callback => {
        callbackRef.current = callback;
    }, []);

    return {
        openPopup,
        redirectPopup,
        closePopup,
        registerCallback,
    };
}
